/**
 * Player state, movement and physics.
 *
 * Pure maths - no camera, no DOM, no engine. The renderer reads `position`
 * plus `eyeHeight` to place its camera; the platform layer feeds in input;
 * the world is reached only through a `SolidTest` callback.
 */

import { moveAABB, type SolidTest, type FillTest } from "../physics/voxelCollision.js";

/** Is the block at these coordinates a fluid you can swim in? */
export type FluidTest = (x: number, y: number, z: number) => boolean;

export interface InputState {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
  sneak: boolean;
  sprint: boolean;
  /**
   * Навприсядки: held, not toggled.
   *
   * Not `sneak`, which in this game means run - Shift is the key people
   * reach for to go faster and it was wired to that long ago. This is its
   * own thing with its own key, and it does what sneaking does in other
   * games: you go slowly, you sit low, and you cannot walk off a block.
   */
  crouch: boolean;
}

export const EMPTY_INPUT: InputState = {
  forward: false, back: false, left: false, right: false,
  jump: false, sneak: false, sprint: false, crouch: false,
};

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export type MoveMode = "walk" | "fly";

// --- body ------------------------------------------------------------------
/** Slightly under 1 so you fit through a one-block gap without catching. */
export const PLAYER_WIDTH = 0.6;
export const PLAYER_HEIGHT = 1.8;
/** Camera sits below the top of the head, as in most first-person games. */
export const EYE_HEIGHT = 1.62;

/**
 * How tall she is with her knees bent, and where she looks from.
 *
 * Three quarters of standing height. Enough that the world visibly drops -
 * crouching you cannot see over what you could see over standing - and not
 * so little that walking about feels like being a rabbit.
 */
export const CROUCH_HEIGHT = 1.35;
export const CROUCH_EYE = 1.2;
/**
 * And how fast, which is the other half of "навприсядки".
 *
 * Slower than a walk by a good margin: crouching is for placing a block at
 * the edge of a drop and for creeping, and at walking speed neither reads as
 * either.
 */
export const CROUCH_SPEED = 1.5;

/**
 * Radians of turn per pixel of mouse.
 *
 * Exported because the drone needs the same number and had none: it took the
 * raw pixel delta as radians, so a hand's width of mouse spun it a dozen
 * times - "крутиться як скажений". Two things you look through have to turn
 * at the same rate, or the drone is a different game with a different mouse.
 */
export const LOOK_SENSITIVITY = 0.0022;
/**
 * How high a ledge can be walked onto without jumping.
 *
 * A feel decision worth knowing about. Minecraft uses 0.6, which only clears
 * a slab - every single-block rise in the terrain needs a jump. With nothing
 * but full blocks in the world yet, that would mean hopping constantly across
 * rolling ground, so this clears one full block instead. Set it to 0.6 for
 * Minecraft's feel; a 2-block wall stops you either way.
 */
const STEP_HEIGHT = 1.05;

// --- walking ---------------------------------------------------------------
export const WALK_SPEED = 4.3;   // blocks/second
/**
 * Running.
 *
 * It was 5.8 - a third faster than walking, which is not running, it is
 * walking briskly. Nobody noticed the game had it. Seven is over half again
 * as fast, which you feel in the first stride, and it is what the camera cue
 * in the renderer is there to confirm.
 */
export const SPRINT_SPEED = 7.0;
const GRAVITY = 32;       // blocks/second^2
/** v = sqrt(2*g*h): 8.95 with g=32 clears a 1.25-block rise. */
const JUMP_VELOCITY = 8.95;
const TERMINAL_VELOCITY = 60;
/** Exponential approach rates. Ground is snappy; air is deliberately not. */
const GROUND_DAMPING = 14;
const AIR_DAMPING = 2.2;
/**
 * Grace period after walking off an edge during which a jump still works.
 * Players press jump slightly late constantly; without this it feels broken.
 */
const COYOTE_TIME = 0.1;

// --- swimming --------------------------------------------------------------
/** Water holds you up: you sink, but slowly enough to swim out of a lake. */
const WATER_GRAVITY_SCALE = 0.26;
const WATER_TERMINAL_VELOCITY = 5;
/** Holding jump swims upward rather than launching you. */
const SWIM_UP_SPEED = 4.2;
const SWIM_SPEED_SCALE = 0.58;
/** Sluggish, so entering water feels like a change of medium. */
const WATER_DAMPING = 7;
/** Height above the feet used to decide "standing in water". */
const WADE_HEIGHT = 0.4;

// --- flying ----------------------------------------------------------------
export const FLY_SPEED = 10;
export const FLY_SPRINT_MULT = 2.8;
const FLY_DAMPING = 12;

const PITCH_LIMIT = Math.PI / 2 - 0.01;

export class PlayerController {
  /** Position of the feet, centred horizontally on the body. */
  readonly position: Vec3 = { x: 0, y: 0, z: 0 };
  readonly velocity: Vec3 = { x: 0, y: 0, z: 0 };

  /** Radians. Yaw around +Y, pitch around the local right axis. */
  yaw = 0;
  pitch = 0;

  /** Fly mode ignores gravity and passes through blocks - it is the dev mode. */
  mode: MoveMode = "walk";

  onGround = false;

  /**
   * How fast this body was falling at the instant it landed, or 0.
   *
   * Set for the one tick the landing happens on and cleared afterwards, so a
   * caller that reads it every tick sees each landing exactly once. Recording
   * the *speed* rather than the height fallen keeps the mover from having to
   * remember anything: it already knows how fast it was going, and water and
   * a slope both change what a given height is worth.
   */
  landingSpeed = 0;

  /** Body is in water: swimming physics apply. */
  inWater = false;
  /** Running *and* going somewhere. Read by the renderer for the camera cue. */
  running = false;
  /** Eyes are under the surface: the view goes murky. */
  submerged = false;

  /**
   * Навприсядки, and it is a state rather than a key.
   *
   * Held down it is on; let go it stays on while there is something directly
   * overhead, because standing up into a ceiling would put her head inside a
   * block. That is the one rule a crouch needs beyond the obvious, and every
   * game with a crouch has it.
   */
  crouching = false;

  /** How tall she is right now: standing, or with her knees bent. */
  get height(): number {
    return this.crouching ? CROUCH_HEIGHT : PLAYER_HEIGHT;
  }

  /** And where she looks from, which follows the same two numbers. */
  get eyeHeight(): number {
    return this.crouching ? CROUCH_EYE : EYE_HEIGHT;
  }

  private coyote = 0;

  toggleFly(): void {
    this.mode = this.mode === "walk" ? "fly" : "walk";
    if (this.mode === "fly") {
      this.velocity.y = 0;
      this.onGround = false;
    }
  }

  /** Unit vector the player is looking along. */
  lookDirection(): Vec3 {
    const cosPitch = Math.cos(this.pitch);
    return {
      x: -Math.sin(this.yaw) * cosPitch,
      y: Math.sin(this.pitch),
      z: -Math.cos(this.yaw) * cosPitch,
    };
  }

  /** Where the camera sits: eye height above the feet. */
  eyePosition(): Vec3 {
    return {
      x: this.position.x,
      // Her own eye height, which is lower on her haunches - every caller
      // gets the crouch for nothing, including the camera, what she can
      // reach and what she can see over.
      y: this.position.y + this.eyeHeight,
      z: this.position.z,
    };
  }

  /** Mouse look. `dx`/`dy` are raw pixel deltas. */
  look(dx: number, dy: number, sensitivity = LOOK_SENSITIVITY): void {
    this.yaw -= dx * sensitivity;
    this.pitch -= dy * sensitivity;
    if (this.pitch > PITCH_LIMIT) this.pitch = PITCH_LIMIT;
    if (this.pitch < -PITCH_LIMIT) this.pitch = -PITCH_LIMIT;
  }

  /** Horizontal movement direction from input and yaw, normalised. */
  private wishDirection(input: InputState): { x: number; z: number } {
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);

    let x = 0;
    let z = 0;
    if (input.forward) { x -= sin; z -= cos; }
    if (input.back)    { x += sin; z += cos; }
    if (input.left)    { x -= cos; z += sin; }
    if (input.right)   { x += cos; z -= sin; }

    // Normalise so moving diagonally isn't faster than moving straight.
    const len = Math.hypot(x, z);
    if (len > 0) { x /= len; z /= len; }
    return { x, z };
  }

  /**
   * Advance one fixed timestep.
   *
   * `dt` is fixed by the caller rather than tied to frame time, so movement
   * behaves identically at 30 fps and 144 fps.
   */
  update(
    input: InputState,
    dt: number,
    isSolid: SolidTest,
    isFluid: FluidTest = () => false,
    /**
     * How full each cell is, where the caller knows about shapes.
     *
     * Left out, every solid cell is full to the brim, which is what they all
     * were before there were shapes at all. See `FillTest`.
     */
    fillAt?: FillTest,
  ): void {
    this.fillAt = fillAt;
    // Sampled before moving, so this frame's physics matches this frame's view.
    this.inWater = isFluid(
      Math.floor(this.position.x),
      Math.floor(this.position.y + WADE_HEIGHT),
      Math.floor(this.position.z),
    );
    this.submerged = isFluid(
      Math.floor(this.position.x),
      Math.floor(this.position.y + EYE_HEIGHT),
      Math.floor(this.position.z),
    );

    if (this.mode === "fly") {
      this.updateFly(input, dt, isSolid);
      return;
    }
    this.updateWalk(input, dt, isSolid);
  }

  /**
   * Whether flying bumps into blocks. Off, flying goes through everything -
   * Виживання's dev mode, and the way out of a bad spot. On, it is a
   * builder's flight, as in the kids' «Творити»: "гравець ходить крізь
   * блоки", and a builder should not.
   */
  flyCollides = false;
  /**
   * How much faster than usual the body walks and runs: 1 always in
   * Виживання; in the kids' game Повітруля's speed potion makes it more
   * for a while.
   */
  speedScale = 1;

  private updateFly(input: InputState, dt: number, isSolid: SolidTest): void {
    const wish = this.wishDirection(input);
    const speed = FLY_SPEED * (input.sprint ? FLY_SPRINT_MULT : 1);

    let vy = 0;
    if (input.jump) vy += speed;
    if (input.sneak) vy -= speed;

    const t = 1 - Math.exp(-FLY_DAMPING * dt);
    this.velocity.x += (wish.x * speed - this.velocity.x) * t;
    this.velocity.y += (vy - this.velocity.y) * t;
    this.velocity.z += (wish.z * speed - this.velocity.z) * t;

    if (this.flyCollides) {
      const result = moveAABB(
        this.position.x, this.position.y, this.position.z,
        PLAYER_WIDTH / 2, this.height,
        this.velocity.x * dt, this.velocity.y * dt, this.velocity.z * dt,
        isSolid, 0, false, this.fillAt,
      );
      this.position.x = result.x;
      this.position.y = result.y;
      this.position.z = result.z;
      this.onGround = result.onGround;
      if (result.hitX) this.velocity.x = 0;
      if (result.hitZ) this.velocity.z = 0;
      if (result.onGround || result.hitCeiling) this.velocity.y = 0;
      this.running = false;
      return;
    }
    // No collision in fly mode - it doubles as the way out of a bad spot.
    this.position.x += this.velocity.x * dt;
    this.position.y += this.velocity.y * dt;
    this.position.z += this.velocity.z * dt;
    this.onGround = false;
    // Flying is not running, and the camera should not pretend otherwise -
    // without this the view stays wide from whenever you last ran.
    this.running = false;
  }

  /** Set by `update`, read by the move below it. See `FillTest`. */
  private fillAt: FillTest | undefined;

  /**
   * Is there anything under her feet at this spot?
   *
   * Asked of the four corners of the body, and true if *any* of them has
   * ground under it: crouched at the lip of a drop you may hang half over
   * the edge, which is what makes the thing useful for building out over
   * nothing. Only the whole body off the block counts as off.
   */
  private footing(
    x: number, y: number, z: number, isSolid: SolidTest,
  ): boolean {
    const half = PLAYER_WIDTH / 2 - 0.001;
    const under = Math.floor(y - 0.06);
    for (const dx of [-half, half]) {
      for (const dz of [-half, half]) {
        if (isSolid(Math.floor(x + dx), under, Math.floor(z + dz))) return true;
      }
    }
    return false;
  }

  /**
   * Down while the key is held, and while she cannot stand up again.
   *
   * Room to stand is asked of the two cells over her head across the whole
   * width of her body: her shoulders are wider than the point she stands on,
   * and a crouch under the lip of a ledge would otherwise pop her into it.
   */
  private updateCrouch(input: InputState, isSolid: SolidTest): void {
    if (input.crouch) {
      this.crouching = true;
      return;
    }
    if (!this.crouching) return;
    const half = PLAYER_WIDTH / 2 - 0.001;
    const feet = this.position.y;
    for (let up = CROUCH_HEIGHT; up < PLAYER_HEIGHT; up += 0.5) {
      const y = Math.floor(feet + Math.min(up, PLAYER_HEIGHT - 0.01));
      for (const dx of [-half, half]) {
        for (const dz of [-half, half]) {
          if (isSolid(Math.floor(this.position.x + dx), y,
            Math.floor(this.position.z + dz))) return;
        }
      }
    }
    this.crouching = false;
  }

  private updateWalk(input: InputState, dt: number, isSolid: SolidTest): void {
    this.updateCrouch(input, isSolid);
    const wish = this.wishDirection(input);
    /*
     * Shift runs, as well as Ctrl and R.
     *
     * Shift is the key a person reaches for without being told, and on the
     * ground it did nothing whatsoever: `sneak` is only read in fly mode,
     * where it means descend. So somebody who tried to run held a key that
     * was not connected to anything and concluded the game had no running in
     * it. It keeps its own meaning in the air, where Shift is still down.
     */
    const running = (input.sprint || input.sneak) && !this.crouching;
    const speed = (this.crouching ? CROUCH_SPEED
      : running ? SPRINT_SPEED : WALK_SPEED) *
      (this.inWater ? SWIM_SPEED_SCALE : 1) * this.speedScale;
    /*
     * Running, as something you can see rather than only measure.
     *
     * Only while actually going somewhere on dry land - holding Shift still
     * or treading water is not a run, and a view that widened for it would
     * read as a fault.
     */
    this.running = running && this.onGround && !this.inWater &&
      (wish.x !== 0 || wish.z !== 0);

    // Air control is deliberately weak: you commit to a jump when you take it.
    // Water is sluggish in every direction, including on the ground.
    const damping = this.inWater
      ? WATER_DAMPING
      : (this.onGround ? GROUND_DAMPING : AIR_DAMPING);
    const t = 1 - Math.exp(-damping * dt);
    this.velocity.x += (wish.x * speed - this.velocity.x) * t;
    this.velocity.z += (wish.z * speed - this.velocity.z) * t;

    if (this.inWater) {
      // Jump becomes a continuous swim stroke rather than a single impulse.
      if (input.jump) {
        this.velocity.y += (SWIM_UP_SPEED - this.velocity.y) * t;
      } else {
        this.velocity.y -= GRAVITY * WATER_GRAVITY_SCALE * dt;
      }
      if (this.velocity.y < -WATER_TERMINAL_VELOCITY) {
        this.velocity.y = -WATER_TERMINAL_VELOCITY;
      }
      this.coyote = 0;
    } else {
      // Jumping, with a short grace period after leaving the ground.
      this.coyote = this.onGround ? COYOTE_TIME : Math.max(0, this.coyote - dt);
      if (input.jump && this.coyote > 0) {
        this.velocity.y = JUMP_VELOCITY;
        this.coyote = 0;
        this.onGround = false;
      }

      this.velocity.y -= GRAVITY * dt;
      if (this.velocity.y < -TERMINAL_VELOCITY) this.velocity.y = -TERMINAL_VELOCITY;
    }

    const was = { x: this.position.x, y: this.position.y, z: this.position.z };
    const result = moveAABB(
      this.position.x, this.position.y, this.position.z,
      PLAYER_WIDTH / 2, this.height,
      this.velocity.x * dt, this.velocity.y * dt, this.velocity.z * dt,
      isSolid,
      // Crouched, she steps onto what is level with her and nothing else:
      // "тільки переходити на інші блоки на тому ж рівні".
      this.crouching ? 0 : STEP_HEIGHT,
      // Stepping is for walking: in the air there is nothing to push off.
      // Swimming there is - the water, the bank: a swimmer climbs out onto
      // a ledge a block up, or "неможливо вилізти з колодязя".
      this.onGround || this.inWater,
      this.fillAt,
    );

    this.position.x = result.x;
    this.position.y = result.y;
    this.position.z = result.z;

    /*
     * Crouched, she does not walk off the edge.
     *
     * Tried one axis at a time, and in that order: sliding along a lip is
     * the whole point - you shuffle sideways to the corner, you do not stop
     * dead the moment one of the two directions would drop you. Only when
     * neither axis on its own is safe does she stay put.
     */
    if (this.crouching && this.onGround && !this.inWater &&
        !this.footing(this.position.x, this.position.y, this.position.z, isSolid)) {
      if (this.footing(was.x, this.position.y, this.position.z, isSolid)) {
        this.position.x = was.x;
        this.velocity.x = 0;
      } else if (this.footing(this.position.x, this.position.y, was.z, isSolid)) {
        this.position.z = was.z;
        this.velocity.z = 0;
      } else {
        this.position.x = was.x;
        this.position.z = was.z;
        this.velocity.x = 0;
        this.velocity.z = 0;
      }
    }

    // The instant of arrival, before the velocity into the ground is zeroed
    // below - which is the only moment the speed still exists.
    this.landingSpeed = !this.onGround && result.onGround && this.velocity.y < 0
      ? -this.velocity.y
      : 0;
    this.onGround = result.onGround;

    // Zero out velocity into surfaces, otherwise it accumulates while held
    // against a wall and you shoot off the moment you turn away.
    if (result.hitX) this.velocity.x = 0;
    if (result.hitZ) this.velocity.z = 0;
    if (result.onGround || result.hitCeiling) this.velocity.y = 0;
  }
}
